import React, { useState } from 'react';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import { TextField, Button, Box, Stack, Typography, IconButton } from '@mui/material';
import { addTodo, updateTodo } from '../../../../slices/todo/todoSlice';
import { selectUser } from '../../../../slices/user/userSlice';
import { useSelector } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { useTranslation } from 'react-i18next';

interface AddTodoFormProps {
  existingTodo?: {
    id: number;
    message: string;
    due_at: string | null;
    started_at: string | null;
    completed_at: string | null;
  };
  onClose?: () => void;
}

const TodoForm = ({ existingTodo, onClose }: AddTodoFormProps) => {
  const dispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const [message, setMessage] = useState(existingTodo?.message || '');
  const [dueAt, setDueAt] = useState<string | null>(existingTodo?.due_at || null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messageError, setMessageError] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');
  const { t } = useTranslation();
  const localTheme = createTheme({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!message) {
      setMessageError('Please enter a task description');
    } else {
      setMessageError('');
    }

    if (!message) return;

    const todoData = {
      user: user.id,
      message,
      due_at: dueAt,
      started_at: new Date().toISOString(),
      completed_at: completedAt,
    };

    if (existingTodo) {
      dispatch(updateTodo({ id: existingTodo?.id, data: todoData }));
    } else {
      dispatch(addTodo({ data: todoData }));
    }

    setMessage('');
    setDueAt('');
    setCompletedAt(null);

    if (onClose) {
      onClose();
    }
  };
  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    document.dispatchEvent(new Event('closeModal'));
  };

  return (
    <ThemeProvider theme={localTheme}>
      <form onSubmit={handleSubmit} className='todo-form'>
        <Box className='todo-header'>
          <Typography variant='h5' component='h2'>
            To do:
          </Typography>
          <IconButton
            id='todoFormCloseIconBtn'
            className='close-button'
            onClick={onClose}
            aria-label='close'
          >
            <CloseIcon id='todoFormCloseIconBtnCloseIcon' />
          </IconButton>
        </Box>
        <Stack component='div' spacing={3}>
          <Box>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
              {t('messages.todoLinkTip')}
            </Typography>
            <TextField
              fullWidth
              label='Task *'
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (isSubmitted) setMessageError('');
              }}
              placeholder='Add a message'
              variant='outlined'
              size='medium'
              className='task-field'
              error={!!messageError}
            />
          </Box>

          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: '100%' }}
                label='Complete by'
                value={dueAt ? dayjs(dueAt) : null}
                onChange={(newValue) => {
                  setDueAt(newValue ? newValue.format('YYYY-MM-DD') : null);
                  if (isSubmitted) setDateError('');
                }}
                format='DD.MM.YYYY'
                className='date-field'
                slotProps={{
                  textField: {
                    error: !!dateError,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box className='form-actions'>
            <Button
              id='todoFormCancelBtn'
              variant='text'
              className='cancel-button'
              onClick={handleCancel}
            >
              {t('buttons.cancel')}
            </Button>
            {existingTodo && (
              <Button
                id='todoFormChangeBtn'
                variant='contained'
                type='submit'
                className='submit-button btn-action-basic'
              >
                {t('buttons.change')}
              </Button>
            )}
            {!existingTodo && (
              <Button
                id='todoFormAddBtn'
                variant='contained'
                type='submit'
                className='submit-button btn-action-basic'
              >
                {t('buttons.add')}
              </Button>
            )}
          </Box>
        </Stack>
      </form>
    </ThemeProvider>
  );
};

export default TodoForm;
